import { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

gsap.registerPlugin(ScrollTrigger);

const SafetySection = () => {
  const sectionRef = useRef(null);
  const textRef = useRef(null);
  const imageRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Text animation
      gsap.fromTo(textRef.current,
        {
          x: -100,
          opacity: 0
        },
        {
          x: 0,
          opacity: 1,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: textRef.current,
            start: "top 80%",
            toggleActions: "play none none reverse"
          }
        }
      );

      // Image animation
      gsap.fromTo(imageRef.current,
        {
          x: 100,
          opacity: 0,
          scale: 0.8
        },
        {
          x: 0,
          opacity: 1,
          scale: 1,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: imageRef.current,
            start: "top 80%",
            toggleActions: "play none none reverse"
          }
        }
      );

      // Floating animation for image
      gsap.to(imageRef.current, {
        y: -20,
        duration: 2,
        repeat: -1,
        yoyo: true,
        ease: "power1.inOut"
      });

    }, sectionRef);

    return () => ctx.revert();
  }, []);

  const features = [
    { icon: "🎯", title: "Precision Design", description: "Exact measurements and detailed planning" },
    { icon: "🛡️", title: "Quality Assurance", description: "Rigorous quality checks at every stage" },
    { icon: "⚡", title: "Quick Installation", description: "Efficient and timely project completion" },
    { icon: "🌟", title: "Premium Materials", description: "Only the finest materials used" }
  ];

  const Navigate = useNavigate();

  const handleBookConsultation = () => {
  window.scrollTo({
    top: 0,
    left: 0,
    behavior: 'smooth' // Optional: adds smooth scrolling
  });
  Navigate('/contact');
};

  return (
    <section
      ref={sectionRef}
      className="relative w-full min-h-screen flex items-center justify-center overflow-hidden"
      aria-labelledby="safety-heading"
    >
      {/* Background with gradient overlay */}
      <div 
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: `url(/assets/Images/12l.webp)`,
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundAttachment: "fixed",
        }}
      >
        {/* Enhanced gradient overlays */}
        <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/70"></div>
        <div className="absolute inset-0 bg-gradient-to-l from-orange-600/10 to-transparent"></div>
      </div>

      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute top-20 left-10 w-4 h-4 bg-orange-400 rounded-full opacity-60 animate-pulse"></div>
        <div className="absolute top-40 right-20 w-3 h-3 bg-amber-300 rounded-full opacity-40 animate-pulse delay-1000"></div>
        <div className="absolute bottom-40 left-20 w-2 h-2 bg-orange-300 rounded-full opacity-50 animate-pulse delay-500"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto w-full py-20 px-6 md:px-10 lg:px-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          
          {/* Text Section */}
          <motion.article
            ref={textRef}
            className="text-white space-y-8"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Badge */}
            <motion.div
              className="inline-flex items-center gap-2 bg-orange-500/20 backdrop-blur-sm border border-orange-500/30 px-4 py-2 rounded-full"
              initial={{ scale: 0 }}
              whileInView={{ scale: 1 }}
              transition={{ type: "spring", stiffness: 200, damping: 15 }}
            >
              <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
              <span className="text-orange-300 text-sm font-semibold uppercase tracking-wide">
                Premium Service
              </span>
            </motion.div>

            {/* Main Heading */}
            <header className="space-y-4">
              <h2
                id="safety-heading"
                className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight"
              >
                Ensuring a{" "}
                <span className="bg-gradient-to-r from-orange-400 to-amber-300 bg-clip-text text-transparent">
                  Safe & Perfect
                </span>{" "}
                Experience
              </h2>
              <p className="text-xl text-gray-200 leading-relaxed">
                From innovative designs to flawless installations, we ensure your 
                complete satisfaction and peace of mind at every step.
              </p>
            </header>

            {/* Features Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {features.map((feature, index) => (
                <motion.div
                  key={index}
                  className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 border border-white/10 hover:border-orange-400/30 transition-all duration-300 group"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 + 0.3 }}
                  whileHover={{ scale: 1.02 }}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-2xl group-hover:scale-110 transition-transform duration-300">
                      {feature.icon}
                    </span>
                    <div>
                      <h3 className="font-semibold text-white text-lg">
                        {feature.title}
                      </h3>
                      <p className="text-gray-300 text-sm mt-1">
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA Buttons */}
            <motion.div 
  className="flex flex-col sm:flex-row gap-4 pt-4"
  initial={{ opacity: 0, y: 20 }}
  whileInView={{ opacity: 1, y: 0 }}
  transition={{ delay: 0.6 }}
>
  <motion.button
    onClick={handleBookConsultation}
    className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-8 py-4 rounded-xl font-semibold hover:shadow-2xl hover:shadow-orange-500/25 transition-all duration-300 flex items-center gap-2 group"
    whileHover={{ scale: 1.05 }}
    whileTap={{ scale: 0.95 }}
    aria-label="Get Free Kitchen Design Consultation"
  >
    <span className="flex items-center gap-2">
      Get Free Consultation
      <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
      </svg>
    </span>
  </motion.button>
  
</motion.div>

            {/* Trust Indicators */}
           
          </motion.article>

          {/* Image Section */}
          <motion.div
            ref={imageRef}
            className="relative flex justify-center items-center"
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="relative">
              {/* Main Image */}
              <motion.img
                src="/assets/Images/11l.webp"
                alt="Professional kitchen design and installation process"
                className="relative z-10 rounded-3xl shadow-2xl w-full max-w-md lg:max-w-lg border-4 border-white/20 backdrop-blur-sm"
                whileHover={{ scale: 1.02 }}
                transition={{ type: "spring", stiffness: 300 }}
              />
              
              {/* Floating Elements */}
              <motion.div
                className="absolute -top-4 -right-4 bg-gradient-to-br from-orange-500 to-amber-500 text-white p-4 rounded-2xl shadow-2xl z-20"
                initial={{ scale: 0, rotate: -10 }}
                whileInView={{ scale: 1, rotate: 0 }}
                transition={{ delay: 1, type: "spring" }}
              >
                <div className="text-center">
                  <div className="text-2xl font-bold">★ 5/5</div>
                  <div className="text-xs">Customer Rating</div>
                </div>
              </motion.div>

              <motion.div
                className="absolute -bottom-4 -left-4 bg-white/10 backdrop-blur-sm border border-white/20 text-white p-3 rounded-xl shadow-2xl z-20"
                initial={{ scale: 0, y: 20 }}
                whileInView={{ scale: 1, y: 0 }}
                transition={{ delay: 1.2, type: "spring" }}
              >
                <div className="text-center">
                  <div className="font-semibold">Quick</div>
                  <div className="text-xs">Installation</div>
                </div>
              </motion.div>

              {/* Background Decorative Element */}
              <div className="absolute -inset-4 bg-gradient-to-r from-orange-500/20 to-amber-500/20 rounded-3xl blur-xl -z-10"></div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Bottom Gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent z-0"></div>
    </section>
  );
};

export default SafetySection;













// import { useEffect, useRef } from "react";
// import { gsap } from "gsap";
// import { ScrollTrigger } from "gsap/ScrollTrigger";
// import { motion } from "framer-motion";
// import { useNavigate } from "react-router-dom";
// import "./theme.css"; // Import the CSS file

// gsap.registerPlugin(ScrollTrigger);

// const SafetySection = () => {
//   const sectionRef = useRef(null);
//   const textRef = useRef(null);
//   const imageRef = useRef(null);

//   useEffect(() => {
//     const ctx = gsap.context(() => {
//       // Text animation
//       gsap.fromTo(textRef.current,
//         {
//           x: -100,
//           opacity: 0
//         },
//         {
//           x: 0,
//           opacity: 1,
//           duration: 1,
//           ease: "power3.out",
//           scrollTrigger: {
//             trigger: textRef.current,
//             start: "top 80%",
//             toggleActions: "play none none reverse"
//           }
//         }
//       );

//       // Image animation
//       gsap.fromTo(imageRef.current,
//         {
//           x: 100,
//           opacity: 0,
//           scale: 0.8
//         },
//         {
//           x: 0,
//           opacity: 1,
//           scale: 1,
//           duration: 1,
//           ease: "power3.out",
//           scrollTrigger: {
//             trigger: imageRef.current,
//             start: "top 80%",
//             toggleActions: "play none none reverse"
//           }
//         }
//       );

//       // Floating animation for image
//       gsap.to(imageRef.current, {
//         y: -20,
//         duration: 2,
//         repeat: -1,
//         yoyo: true,
//         ease: "power1.inOut"
//       });

//     }, sectionRef);

//     return () => ctx.revert();
//   }, []);

//   const features = [
//     { icon: "🎯", title: "Precision Design", description: "Exact measurements and detailed planning" },
//     { icon: "🛡️", title: "Quality Assurance", description: "Rigorous quality checks at every stage" },
//     { icon: "⚡", title: "Quick Installation", description: "Efficient and timely project completion" },
//     { icon: "🌟", title: "Premium Materials", description: "Only the finest materials used" }
//   ];

//   const Navigate = useNavigate();

//   const handleBookConsultation = () => {
//     window.scrollTo({
//       top: 0,
//       left: 0,
//       behavior: 'smooth'
//     });
//     Navigate('/contact');
//   };

//   return (
//     <section
//       ref={sectionRef}
//       className="relative w-full min-h-screen flex items-center justify-center overflow-hidden"
//       aria-labelledby="safety-heading"
//     >
//       {/* Background with gradient overlay */}
//       <div 
//         className="absolute inset-0 z-0"
//         style={{
//           backgroundImage: `url(/assets/Images/12l.webp)`,
//           backgroundSize: "cover",
//           backgroundPosition: "center",
//           backgroundAttachment: "fixed",
//         }}
//       >
//         {/* Enhanced gradient overlays */}
//         <div 
//           className="absolute inset-0"
//           style={{
//             background: "var(--overlay-dark-gradient)",
//           }}
//         ></div>
//         <div 
//           className="absolute inset-0"
//           style={{
//             background: "var(--overlay-vertical-gradient)",
//           }}
//         ></div>
//         <div 
//           className="absolute inset-0"
//           style={{
//             background: "var(--overlay-primary-light)",
//           }}
//         ></div>
//       </div>

//       {/* Animated background elements */}
//       <div className="absolute inset-0 overflow-hidden">
//         <div 
//           className="absolute top-20 left-10 w-4 h-4 rounded-full opacity-60 animate-pulse"
//           style={{
//             backgroundColor: "var(--color-primary-400)",
//           }}
//         ></div>
//         <div 
//           className="absolute top-40 right-20 w-3 h-3 rounded-full opacity-40 animate-pulse delay-1000"
//           style={{
//             backgroundColor: "var(--color-amber-300)",
//           }}
//         ></div>
//         <div 
//           className="absolute bottom-40 left-20 w-2 h-2 rounded-full opacity-50 animate-pulse delay-500"
//           style={{
//             backgroundColor: "var(--color-primary-300)",
//           }}
//         ></div>
//       </div>

//       <div className="relative z-10 max-w-7xl mx-auto w-full py-20 px-6 md:px-10 lg:px-16">
//         <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          
//           {/* Text Section */}
//           <motion.article
//             ref={textRef}
//             className="space-y-8"
//             initial={{ opacity: 0, x: -50 }}
//             whileInView={{ opacity: 1, x: 0 }}
//             transition={{ duration: 0.8 }}
//             style={{
//               color: "var(--color-white)",
//             }}
//           >
//             {/* Badge */}
//             <motion.div
//               className="inline-flex items-center gap-2 backdrop-blur-sm px-4 py-2 rounded-full"
//               initial={{ scale: 0 }}
//               whileInView={{ scale: 1 }}
//               transition={{ type: "spring", stiffness: 200, damping: 15 }}
//               style={{
//                 backgroundColor: "var(--bg-primary-500-20)",
//                 border: "1px solid var(--border-primary-30)",
//               }}
//             >
//               <div 
//                 className="w-2 h-2 rounded-full animate-pulse"
//                 style={{
//                   backgroundColor: "var(--color-primary-400)",
//                 }}
//               ></div>
//               <span 
//                 className="text-sm font-semibold uppercase tracking-wide"
//                 style={{
//                   color: "var(--color-amber-300)",
//                 }}
//               >
//                 Premium Service
//               </span>
//             </motion.div>

//             {/* Main Heading */}
//             <header className="space-y-4">
//               <h2
//                 id="safety-heading"
//                 className=" bell-mt-primary text-4xl md:text-5xl lg:text-6xl font-bold leading-tight"
//                 style={{
//                   color: "var(--color-white)",
//                 }}
//               >
//                 Ensuring a{" "}
//                 <span 
//                   className=" bell-mt-primary bg-clip-text text-transparent"
//                   style={{
//                     background: "var(--gradient-primary)",
//                     WebkitBackgroundClip: "text",
//                     backgroundClip: "text",
//                   }}
//                 >
//                   Safe & Perfect
//                 </span>{" "}
//                 Experience
//               </h2>
//               <p 
//                 className="  text-xl leading-relaxed"
//                 style={{
//                   color: "var(--color-gray-200)",
//                 }}
//               >
//                 From innovative designs to flawless installations, we ensure your 
//                 complete satisfaction and peace of mind at every step.
//               </p>
//             </header>

//             {/* Features Grid */}
//             <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
//               {features.map((feature, index) => (
//                 <motion.div
//                   key={index}
//                   className="backdrop-blur-sm rounded-2xl p-4 transition-all duration-300 group"
//                   initial={{ opacity: 0, y: 20 }}
//                   whileInView={{ opacity: 1, y: 0 }}
//                   transition={{ delay: index * 0.1 + 0.3 }}
//                   whileHover={{ scale: 1.02 }}
//                   style={{
//                     backgroundColor: "var(--bg-white-10)",
//                     border: "1px solid var(--border-white-10)",
//                   }}
//                 >
//                   <div className="flex items-start gap-3">
//                     <span className="text-2xl group-hover:scale-110 transition-transform duration-300">
//                       {feature.icon}
//                     </span>
//                     <div>
//                       <h3 
//                         className="font-semibold text-lg"
//                         style={{
//                           color: "var(--color-white)",
//                         }}
//                       >
//                         {feature.title}
//                       </h3>
//                       <p 
//                         className="text-sm mt-1"
//                         style={{
//                           color: "var(--color-gray-300)",
//                         }}
//                       >
//                         {feature.description}
//                       </p>
//                     </div>
//                   </div>
//                 </motion.div>
//               ))}
//             </div>

//             {/* CTA Buttons */}
//             <motion.div 
//               className="flex flex-col sm:flex-row gap-4 pt-4"
//               initial={{ opacity: 0, y: 20 }}
//               whileInView={{ opacity: 1, y: 0 }}
//               transition={{ delay: 0.6 }}
//             >
//               <motion.button
//                 onClick={handleBookConsultation}
//                 className="text-white px-8 py-4 rounded-xl font-semibold transition-all duration-300 flex items-center gap-2 group"
//                 whileHover={{ scale: 1.05 }}
//                 whileTap={{ scale: 0.95 }}
//                 aria-label="Get Free Kitchen Design Consultation"
//                 style={{
//                   background: "var(--gradient-primary)",
//                 }}
//               >
//                 <span className="flex items-center gap-2">
//                   Get Free Consultation
//                   <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
//                     <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
//                   </svg>
//                 </span>
//               </motion.button>
//             </motion.div>
//           </motion.article>

//           {/* Image Section */}
//           <motion.div
//             ref={imageRef}
//             className="relative flex justify-center items-center"
//             initial={{ opacity: 0, x: 50 }}
//             whileInView={{ opacity: 1, x: 0 }}
//             transition={{ duration: 0.8, delay: 0.2 }}
//           >
//             <div className="relative">
//               {/* Main Image */}
//               <motion.img
//                 src="/assets/Images/11l.webp"
//                 alt="Professional kitchen design and installation process"
//                 className="relative z-10 rounded-3xl w-full max-w-md lg:max-w-lg backdrop-blur-sm"
//                 whileHover={{ scale: 1.02 }}
//                 transition={{ type: "spring", stiffness: 300 }}
//                 style={{
//                   border: "4px solid var(--border-white-10)",
//                   boxShadow: "var(--shadow-large)",
//                 }}
//               />
              
//               {/* Floating Elements */}
//               <motion.div
//                 className="absolute -top-4 -right-4 text-white p-4 rounded-2xl z-20"
//                 initial={{ scale: 0, rotate: -10 }}
//                 whileInView={{ scale: 1, rotate: 0 }}
//                 transition={{ delay: 1, type: "spring" }}
//                 style={{
//                   background: "var(--gradient-primary)",
//                   boxShadow: "var(--shadow-large)",
//                 }}
//               >
//                 <div className="text-center">
//                   <div className="text-2xl font-bold">★ 5/5</div>
//                   <div className="text-xs">Customer Rating</div>
//                 </div>
//               </motion.div>

//               <motion.div
//                 className="absolute -bottom-4 -left-4 backdrop-blur-sm text-white p-3 rounded-xl z-20"
//                 initial={{ scale: 0, y: 20 }}
//                 whileInView={{ scale: 1, y: 0 }}
//                 transition={{ delay: 1.2, type: "spring" }}
//                 style={{
//                   backgroundColor: "var(--overlay-white-10)",
//                   border: "1px solid var(--border-white-20)",
//                   boxShadow: "var(--shadow-large)",
//                 }}
//               >
//                 <div className="text-center">
//                   <div className="font-semibold">Quick</div>
//                   <div className="text-xs">Installation</div>
//                 </div>
//               </motion.div>

//               {/* Background Decorative Element */}
//               <div 
//                 className="absolute -inset-4 rounded-3xl blur-xl -z-10"
//                 style={{
//                   background: "linear-gradient(to right, var(--color-primary-500), var(--color-amber-500), var(--color-primary-500))",
//                   opacity: 0.2,
//                 }}
//               ></div>
//             </div>
//           </motion.div>
//         </div>
//       </div>

//       {/* Bottom Gradient */}
//       <div 
//         className="absolute bottom-0 left-0 right-0 h-32 z-0"
//         style={{
//           background: "linear-gradient(to top, var(--color-black) 0%, transparent 100%)",
//         }}
//       ></div>
//     </section>
//   );
// };

// export default SafetySection;










// import React, { useEffect, useRef, useState } from "react";
// import { gsap } from "gsap";
// import { ScrollTrigger } from "gsap/ScrollTrigger";
// import { motion } from "framer-motion";
// import { Star, ChevronRight, Quote, MessageSquare, ThumbsUp, ChevronDown } from "lucide-react";
// import "./theme.css";

// gsap.registerPlugin(ScrollTrigger);

// const TestimonialsSection = () => {
//   const sectionRef = useRef(null);
//   const marqueeRef = useRef(null);
//   const [isHovering, setIsHovering] = useState(false);
//   const [expandedReviews, setExpandedReviews] = useState({});

//   const GOOGLE_MAPS_LINK = "https://maps.app.goo.gl/mfQxTonXebrLAmw98";

//   const sampleReviews = [
//     {
//       id: 1,
//       author_name: "Kousik Bera",
//       rating: 5,
//       text: "Laminance kitchen did an exceptional job in making out dreams of an unique kind of kitchen in USA. We wanted the European style kitchen and not the traditional shaker style kitchen. He made that possible. Laminance has accommodated all our needs and our dream kitchen speaks for itself. The attention to detail and quality of craftsmanship exceeded our expectations. The team was professional, punctual, and kept the workspace clean throughout the project.",
//       relative_time_description: "2 weeks ago",
//       profile_photo_url: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=400&fit=crop&crop=face",
//       project: "Modern Kitchen Remodel"
//     },
//     {
//       id: 2,
//       author_name: "Abhay Kulkarni",
//       rating: 5,
//       text: "I got introduced to this business while at an event. I found the folks very approachable, not at all pushy or sales men like, rather professional, listening to the folks and working with them per their needs, making suggestions where necessary with proper reasoning for each. I got feedback from other clients that the quality and workmanship is topnotch. Vivek and his team take good care of their clients. I will highly recommend Laminance Cabinetry to anyone looking for quality kitchen solutions.",
//       relative_time_description: "1 month ago",
//       profile_photo_url: "https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?w=400&h=400&fit=crop&crop=face",
//       project: "Contemporary Kitchen"
//     },
//     {
//       id: 3,
//       author_name: "Vikalp Chouhan",
//       rating: 5,
//       text: "Laminance Cabinetry are one of the best in NJ area . Best service , quality and looks . They have very competitive prices and are a must visit shop if you are looking for Kitchen upgrades. Their showroom displays various styles and finishes that helped us visualize our final kitchen design perfectly.",
//       relative_time_description: "3 weeks ago",
//       profile_photo_url: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400&h=400&fit=crop&crop=face",
//       project: "Complete Kitchen Makeover"
//     },
//   ];

//   const duplicatedReviews = [...sampleReviews, ...sampleReviews];

//   useEffect(() => {
//     const ctx = gsap.context(() => {
//       // Title animation
//       gsap.from(".section-title", {
//         opacity: 0,
//         y: 40,
//         duration: 1,
//         ease: "power3.out",
//         scrollTrigger: {
//           trigger: ".section-title",
//           start: "top 85%",
//         }
//       });

//       // Stats animation
//       gsap.from(".stat-item", {
//         y: 30,
//         opacity: 0,
//         duration: 0.8,
//         stagger: 0.15,
//         ease: "power3.out",
//         scrollTrigger: {
//           trigger: ".stats-container",
//           start: "top 80%",
//         }
//       });

//     }, sectionRef);

//     return () => ctx.revert();
//   }, []);

//   // Close all expanded reviews when marquee starts scrolling
//   useEffect(() => {
//     if (!isHovering && Object.keys(expandedReviews).length > 0) {
//       setExpandedReviews({});
//     }
//   }, [isHovering, expandedReviews]);

//   const toggleReviewExpansion = (reviewId) => {
//     setExpandedReviews(prev => ({
//       ...prev,
//       [reviewId]: !prev[reviewId]
//     }));
//   };

//   const renderStars = (rating) => {
//     return (
//       <div className="flex gap-0.5">
//         {Array.from({ length: 5 }, (_, i) => (
//           <Star
//             key={i}
//             size={16}
//             className={`${i < rating ? "fill-current text-yellow-400" : "text-gray-300"}`}
//           />
//         ))}
//       </div>
//     );
//   };

//   const TruncatedText = ({ text, maxLength = 150, reviewId }) => {
//     const isExpanded = expandedReviews[reviewId];
    
//     if (text.length <= maxLength) {
//       return <p className="text-gray-600 leading-relaxed">"{text}"</p>;
//     }

//     const truncatedText = isExpanded ? text : `${text.substring(0, maxLength)}...`;

//     return (
//       <div>
//         <p className="text-gray-600 leading-relaxed">"{truncatedText}"</p>
//         <button
//           onClick={(e) => {
//             e.stopPropagation(); // Prevent triggering mouse leave
//             toggleReviewExpansion(reviewId);
//           }}
//           className="mt-2 flex items-center gap-1 text-sm font-medium transition-colors"
//           style={{ color: "var(--color-primary-400)" }}
//         >
//           <span>{isExpanded ? "Read less" : "Read more"}</span>
//           <motion.div
//             animate={{ rotate: isExpanded ? 180 : 0 }}
//             transition={{ duration: 0.3 }}
//           >
//             <ChevronDown size={14} />
//           </motion.div>
//         </button>
//       </div>
//     );
//   };

//   return (
//     <section
//       ref={sectionRef}
//       className="py-20 relative overflow-hidden"
//       style={{
//         background: "var(--color-gray-50)",
//       }}
//     >
//       {/* Subtle Background Pattern */}
//       <div className="absolute inset-0 opacity-5">
//         <div 
//           className="absolute top-1/4 left-1/4 w-64 h-64 rounded-full"
//           style={{ background: "var(--color-primary-400)" }}
//         ></div>
//         <div 
//           className="absolute bottom-1/4 right-1/4 w-64 h-64 rounded-full"
//           style={{ background: "var(--color-primary-400)" }}
//         ></div>
//       </div>

//       <div className="max-w-6xl mx-auto px-6 relative z-10">
//         {/* Section Header */}
//         <div className="text-center mb-16">
//           <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6"
//             style={{
//               background: "var(--bg-primary-10)",
//               border: "1px solid var(--border-primary-20)",
//             }}
//           >
//             <MessageSquare size={14} style={{ color: "var(--color-primary-400)" }} />
//             <span className="text-sm font-medium" style={{ color: "var(--color-primary-400)" }}>
//               Client Testimonials
//             </span>
//           </div>
          
//           <h2
//             id="services-heading"
//             className="bell-mt-primary services-title text-4xl md:text-5xl lg:text-6xl font-bold bg-clip-text text-transparent mb-6"
//             style={{
//               background: "var(--gradient-primary)",
//               WebkitBackgroundClip: "text",
//               backgroundClip: "text",
//             }}
//           >
//             Extremely High Standard of<br />Customer Satisfaction
//           </h2>
          
//           <p className="text-lg max-w-2xl mx-auto mb-12"
//             style={{ color: "var(--color-gray-600)" }}
//           >
//             See what our clients have to say about their Laminance kitchen transformations.
//           </p>
//         </div>

//         {/* Testimonials Marquee */}
//         <div className="relative mb-20">
//           <div 
//             ref={marqueeRef}
//             className="overflow-hidden py-4"
//             onMouseEnter={() => {
//               setIsHovering(true);
//             }}
//             onMouseLeave={() => {
//               setIsHovering(false);
//             }}
//           >
//             <motion.div 
//               className="flex gap-8"
//               animate={{ 
//                 x: isHovering ? 0 : ['0%', '-100%']
//               }}
//               transition={{
//                 x: {
//                   duration: 50,
//                   ease: "linear",
//                   repeat: Infinity,
//                   repeatType: "loop",
//                 }
//               }}
//             >
//               {duplicatedReviews.map((review, index) => {
//                 const reviewKey = `${review.id}-${index}`;
//                 const isExpanded = expandedReviews[reviewKey];
                
//                 return (
//                   <div 
//                     key={reviewKey} 
//                     className="flex-shrink-0 w-80"
//                     style={{ 
//                       transition: 'height 0.3s ease',
//                       height: isExpanded ? 'auto' : 'fit-content'
//                     }}
//                   >
//                     <div 
//                       className="relative group p-6 rounded-2xl transition-all duration-300 hover:shadow-xl"
//                       style={{
//                         background: "var(--color-white)",
//                         border: "1px solid var(--border-light)",
//                       }}
//                     >
//                       {/* Rating Badge */}
//                       <div className="absolute -top-3 -right-3">
//                         <div className="flex items-center gap-1 px-3 py-1.5 rounded-full"
//                           style={{
//                             background: "var(--color-white)",
//                             border: "1px solid var(--border-light)",
//                             boxShadow: "var(--shadow-medium)",
//                           }}
//                         >
//                           {renderStars(review.rating)}
//                           <span className="text-sm font-medium ml-1" style={{ color: "var(--color-gray-700)" }}>
//                             {review.rating}.0
//                           </span>
//                         </div>
//                       </div>

//                       {/* Review Content */}
//                       <div className="mb-6">
//                         <Quote 
//                           size={24}
//                           className="mb-4 opacity-50"
//                           style={{ color: "var(--color-primary-400)" }}
//                         />
//                         <TruncatedText 
//                           text={review.text} 
//                           maxLength={150}
//                           reviewId={reviewKey}
//                         />
//                       </div>

//                       {/* Reviewer Info */}
//                       <div className="flex items-center gap-3 pt-6 border-t"
//                         style={{ borderColor: "var(--border-light)" }}
//                       >
//                         <div className="w-12 h-12 rounded-full overflow-hidden border-2"
//                           style={{ borderColor: "var(--color-white)", boxShadow: "var(--shadow-light)" }}
//                         >
//                           <img 
//                             src={review.profile_photo_url} 
//                             alt={review.author_name}
//                             className="w-full h-full object-cover"
//                           />
//                         </div>
//                         <div className="flex-1">
//                           <h4 className="font-semibold" style={{ color: "var(--color-gray-600)" }}>
//                             {review.author_name}
//                           </h4>
//                           <div className="flex items-center gap-2">
//                             <span className="text-sm" style={{ color: "var(--color-gray-500)" }}>
//                               {review.relative_time_description}
//                             </span>
//                             <div className="w-1 h-1 rounded-full" style={{ background: "var(--color-gray-300)" }}></div>
//                           </div>
//                         </div>
//                       </div>

//                       {/* Project Info */}
//                       {review.project && (
//                         <div className="mt-4 text-sm px-3 py-2 rounded-lg"
//                           style={{
//                             background: "var(--bg-primary-10)",
//                             color: "var(--color-primary-400)",
//                           }}
//                         >
//                           {review.project}
//                         </div>
//                       )}

//                       {/* Auto-close indicator (visible only when expanded and not hovering) */}
//                       {isExpanded && !isHovering && (
//                         <div className="absolute inset-0 bg-white/50 backdrop-blur-[1px] rounded-2xl flex items-center justify-center z-10">
//                           <div className="text-sm font-medium px-4 py-2 rounded-lg"
//                             style={{
//                               background: "var(--color-primary-400)",
//                               color: "var(--color-white)",
//                             }}
//                           >
//                             Scroll paused for reading...
//                           </div>
//                         </div>
//                       )}
//                     </div>
//                   </div>
//                 );
//               })}
//             </motion.div>
//           </div>

//           {/* Gradient Overlays */}
//           <div className="absolute left-0 top-0 bottom-0 w-24 pointer-events-none"
//             style={{
//               background: "linear-gradient(to right, var(--color-gray-50), transparent)",
//             }}
//           ></div>
//           <div className="absolute right-0 top-0 bottom-0 w-24 pointer-events-none"
//             style={{
//               background: "linear-gradient(to left, var(--color-gray-50), transparent)",
//             }}
//           ></div>
//         </div>

//         {/* CTA Section */}
//         <div className="text-center">
//           <div className="max-w-2xl mx-auto mb-8">
//             <h2 className="bell-mt-primary text-3xl  font-bold mb-4" style={{ color: "var(--color-gray-600)" }}>
//               Ready to Transform Your Kitchen?
//             </h2>
//             <p className="text-gray-600 mb-8">
//               Every Laminance kitchen tells a story of trust, precision, and premium craftsmanship.
//             </p>
//           </div>

//           <div className="flex flex-col sm:flex-row gap-4 justify-center">
//             <motion.a
//               href={GOOGLE_MAPS_LINK}
//               target="_blank"
//               rel="noopener noreferrer"
//               className="px-8 py-4 rounded-xl font-semibold flex items-center justify-center gap-3 group"
//               style={{
//                 background: "var(--color-primary-400)",
//                 color: "var(--color-white)",
//               }}
//               whileHover={{ scale: 1.02 }}
//               whileTap={{ scale: 0.98 }}
//             >
//               <ThumbsUp size={20} />
//               <span>Write a Review</span>
//               <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />
//             </motion.a>
            
//             <motion.a
//               href={GOOGLE_MAPS_LINK}
//               target="_blank"
//               rel="noopener noreferrer"
//               className="px-8 py-4 rounded-xl font-semibold flex items-center justify-center gap-3"
//               style={{
//                 background: "var(--color-white)",
//                 border: "2px solid var(--color-primary-400)",
//                 color: "var(--color-primary-400)",
//               }}
//               whileHover={{ scale: 1.02 }}
//               whileTap={{ scale: 0.98 }}
//             >
//               <span>View All Reviews</span>
//             </motion.a>
//           </div>

//           <div className="mt-8 text-sm" style={{ color: "var(--color-gray-500)" }}>
//             All reviews are verified from Google Business Profile
//           </div>
//         </div>
//       </div>

//       {/* Hover Indicator */}
//       <motion.div
//         className="absolute bottom-10 left-1/2 transform -translate-x-1/2"
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         transition={{ delay: 0.5 }}
//       >
//         <div className="flex items-center gap-2 px-4 py-2 rounded-full"
//           style={{
//             background: "var(--color-white)",
//             border: "1px solid var(--border-light)",
//             boxShadow: "var(--shadow-light)",
//           }}
//         >
//           <div className="text-xs font-medium" style={{ color: "var(--color-gray-600)" }}>
//             Hover to pause reviews
//           </div>
//           <motion.div
//             animate={{ x: [0, 4, 0] }}
//             transition={{ repeat: Infinity, duration: 2 }}
//           >
//             <ChevronRight size={12} style={{ color: "var(--color-gray-400)" }} />
//           </motion.div>
//         </div>
//       </motion.div>
//     </section>
//   );
// };

// export default TestimonialsSection;